import { Box, Typography, TextField, Switch } from "@mui/material";
import DoNotDisturbOnIcon from "@mui/icons-material/DoNotDisturbOn";

interface EnrollmentsPerPersonSectionProps {
	enabled: boolean;
	maximum?: number;
	/** SIMS-9203 — true counts a person's enrollments across every organization unit. */
	acrossOrgUnits?: boolean;
	onEnabledChange: (value: boolean) => void;
	/** `undefined` when the field is cleared — never NaN. */
	onMaximumChange: (value: number | undefined) => void;
	/** SIMS-9203 — omitted by callers that do not render the scope choice. */
	onAcrossOrgUnitsChange?: (value: boolean) => void;
}

/**
 * SIMS-9202 — "Enrollments per person" configuration card (Program Maintenance).
 *
 * The story and register FR-1/D1 specify this section by reference: "the same layout as
 * the existing Budget enforcement section". There is no Figma or hand-off (the Epic
 * declares all three as N/A), so BudgetEnforcementSection is the design source and this
 * component follows its structure — header row with an Enabled/Disabled label and a
 * Switch, an off-state line, and the dependent field revealed only when on.
 *
 * The section is rendered OUTSIDE the Incentive Configuration block, which is the only
 * block the enrollment lock disables (FR-15). That placement is what makes SIMS-9204 AC1
 * true: this stays editable on a program that already has enrollments.
 */
export default function EnrollmentsPerPersonSection({
	enabled,
	maximum,
	acrossOrgUnits,
	onEnabledChange,
	onMaximumChange,
	onAcrossOrgUnitsChange,
}: EnrollmentsPerPersonSectionProps): JSX.Element {
	return (
		<Box
			data-testid="program-enrollments-per-person-section"
			sx={{
				border: "1px solid",
				borderColor: "divider",
				borderRadius: 1,
				p: 2,
			}}
		>
			<Box
				sx={{
					display: "flex",
					alignItems: "flex-start",
					justifyContent: "space-between",
					pb: enabled ? 2 : 1,
					borderBottom: "1px solid",
					borderColor: "divider",
				}}
			>
				<Box>
					<Typography variant="subtitle1" fontWeight={600}>
						Enrollments per person
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Cap how many times the same person can be enrolled in this program.
					</Typography>
				</Box>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Typography
						variant="body2"
						color={enabled ? "primary" : "text.secondary"}
					>
						{enabled ? "Enabled" : "Disabled"}
					</Typography>
					<Switch
						checked={enabled}
						onChange={(e) => onEnabledChange(e.target.checked)}
						inputProps={
							{
								"data-testid": "program-enrollments-per-person-enabled-toggle",
								"aria-label": "Enable Enrollments per person",
							} as React.InputHTMLAttributes<HTMLInputElement>
						}
					/>
				</Box>
			</Box>

			{!enabled && (
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
						color: "text.secondary",
						mt: 1.5,
					}}
				>
					<DoNotDisturbOnIcon fontSize="small" />
					<Typography variant="body2">
						No maximum is applied — a person can be enrolled in this program any
						number of times.
					</Typography>
				</Box>
			)}

			{enabled && (
				<Box
					data-testid="program-enrollments-per-person-config"
					sx={{
						mt: 2.5,
						display: "flex",
						flexDirection: "column",
						gap: 2.5,
						maxWidth: 360,
					}}
				>
					<TextField
						fullWidth
						required
						label="Maximum enrollments per person"
						type="number"
						value={Number.isFinite(maximum) ? maximum : ""}
						onChange={(e) => {
							// Clearing the field makes parseInt("") NaN. Reporting NaN upward
							// would put an invalid number in form state and make the controlled
							// value flip back to "" on the next render, which reads as the field
							// fighting the user. Report undefined instead: the page-level guard
							// already treats a missing value as "required while on" (AC2).
							const parsed = parseInt(e.target.value, 10);
							onMaximumChange(Number.isNaN(parsed) ? undefined : parsed);
						}}
						helperText="A whole number, 1 or more. Required while this is on."
						inputProps={{
							"data-testid": "program-enrollments-per-person-input",
							min: 1,
							step: 1,
						}}
					/>

					{/*
					 * SIMS-9203 — the organization-unit scope choice. Nested inside the
					 * enabled block, which is what makes AC1 and AC4 true: when the feature
					 * is off this is not rendered at all, so it cannot be changed.
					 *
					 * A boolean, not a unit picker. A program spans SEVERAL units
					 * (Program.orgUnitId is an array) while an enrollment has exactly one,
					 * so there is no unit to choose here — "scoped" means the unit of the
					 * enrollment being attempted, resolved at enrollment time (FR-24).
					 */}
					{onAcrossOrgUnitsChange && (
						<Box
							sx={{
								display: "flex",
								alignItems: "flex-start",
								justifyContent: "space-between",
								gap: 2,
							}}
						>
							<Box>
								<Typography variant="body2" fontWeight={500}>
									Count across all organization units
								</Typography>
								<Typography variant="caption" color="text.secondary">
									{acrossOrgUnits
										? "A person's enrollments in every organization unit count toward the maximum."
										: "Only enrollments in the same organization unit count toward the maximum."}
								</Typography>
							</Box>
							<Switch
								checked={acrossOrgUnits ?? false}
								onChange={(e) => onAcrossOrgUnitsChange(e.target.checked)}
								inputProps={
									{
										"data-testid":
											"program-enrollments-per-person-count-across-units",
										"aria-label": "Count across all organization units",
									} as React.InputHTMLAttributes<HTMLInputElement>
								}
							/>
						</Box>
					)}
				</Box>
			)}
		</Box>
	);
}
